import cx from 'classnames';
import PropTypes from 'prop-types';
import {useCallback, useState} from 'react';
import {Divider} from '../../../atoms/Divider';
import {Typography} from '../../../atoms/Typography';
import {useIsExtraLargeAndUp, useIsLargishAndUp} from '../../../hooks/useMatchQuery';
import {AccountLineIcon} from '../../../svg/AccountLineIcon';
import {AlertLineIcon} from '../../../svg/AlertLineIcon';
import {ArrowIcon} from '../../../svg/ArrowIcon';
import {CalendarLineIcon} from '../../../svg/CalendarLineIcon';
import {ClickOutLineIcon} from '../../../svg/ClickOutLineIcon';
import {CloseLineIcon} from '../../../svg/CloseLineIcon';
import {EditLineIcon} from '../../../svg/EditLineIcon';
import {EnvelopeSentIcon} from '../../../svg/EnvelopeSentIcon';
import {FavoritesLineIcon} from '../../../svg/FavoritesLineIcon';
import {GuestListLineIcon} from '../../../svg/GuestListLineIcon';
import {LockLineIcon} from '../../../svg/LockLineIcon';
import {SettingsLineIcon} from '../../../svg/SettingsLineIcon';
import {DocumentIcon} from '../../../svg/v2/DocumentIcon';
import {AcctDetailsProCTA} from '../AccountDetailsProCTA/AccountDetailsProCTA';
import {AcctDrawerItem} from '../AcctDrawerItem/AcctDrawerItem';
import {UserCallout} from './UserCallout';

import './AccountDetailsDrawer.sass';

export const AccountDetailsDrawer = ({user, open, onClose, onLogout}) => {
  const isExtraLargeAndUp = useIsExtraLargeAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const fullName = user.full_name || user.userName;
  const hasUserImage =
    (user.image_url && user.image_url !== 'None') || (user.imgUrl && user.imgUrl !== 'None');
  const userImage = user.image_url || user.imgUrl;

  const [mwAcctSettingsOpen, setMwAcctSettingsOpen] = useState(false);

  /** Handle clicking Account Settings in the MW account details drawer */
  const onMwAcctSettingsClick = useCallback(() => setMwAcctSettingsOpen(true), []);

  /** Handle clicking the back arrow in Account Settings in MW */
  const onMwAcctSettingsBackArrowClick = useCallback(() => {
    setMwAcctSettingsOpen(false);
  }, []);

  return (
    <div
      className={cx('acct-details-drawer', isExtraLargeAndUp ? '' : 'full-height', {open})}
      id="acctDetails"
    >
      {!isExtraLargeAndUp && (
        <>
          {mwAcctSettingsOpen ? (
            <div className="acct-details-drawer__header">
              <button
                className="unstyled-button"
                onClick={onMwAcctSettingsBackArrowClick}
                data-qa-id="closeAccountSettingsButton"
                aria-label="Close account settings"
                type="button"
              >
                <ArrowIcon ratio={0.75} direction="left" />
              </button>
              <Typography as="h3" variant="header3" data-qa-id="accountSettingsHeaderText">
                Account settings
              </Typography>
            </div>
          ) : (
            <div className="acct-details-drawer__header">
              <button
                className="unstyled-button"
                onClick={onClose}
                tabIndex={open ? 0 : -1}
                type="button"
                data-qa-id="closeAccountSettingsButton"
                aria-label="Close account settings"
              >
                <CloseLineIcon ratio={0.75} />
              </button>
              <div className="acct-details-drawer__user-details">
                <UserCallout name={fullName} className="acct-details-drawer__user-name" />
                <div className="nav-avatar big">
                  {hasUserImage ? (
                    <img src={userImage} alt="Profile" />
                  ) : (
                    <Typography
                      as="p"
                      variant="paragraph2"
                      data-qa-id="accountSettingsUserAvatarInitials"
                    >
                      {user.initials}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          )}
          <Divider marginTop="1rem" marginBottom="1rem" />
        </>
      )}
      <div className="acct-details-drawer__cta-group">
        {isExtraLargeAndUp || !mwAcctSettingsOpen ? (
          <>
            {isExtraLargeAndUp ? (
              <UserCallout name={fullName} className="acct-details-drawer__user-name" />
            ) : null}
            <AcctDrawerItem
              href="/my-events"
              text="My Invitations"
              icon={<CalendarLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="myEventsAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/my-events?type=signup-sheets"
              text="My SignUp Sheets"
              icon={<DocumentIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="mySignUpSheetsAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/my-events?type=card"
              text="My Greeting Cards"
              icon={<EnvelopeSentIcon ratio={isExtraLargeAndUp ? 0.54 : 0.72} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="myECardsAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/gallery/favorites"
              text={`My favorites${
                window.favoritesNumber !== undefined ? ` (${window.favoritesNumber})` : ''
              }`}
              icon={<FavoritesLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="myFavoritesAcctDrawerItem"
            />
            {isLargishAndUp && (
              <AcctDrawerItem
                href="/profile/contacts"
                text="Address book"
                icon={<GuestListLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
                tabIndex={open ? 0 : -1}
                data-qa-id="addressBookAcctDrawerItem"
              />
            )}
            <AcctDrawerItem
              icon={<SettingsLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              text="Account settings"
              type={isExtraLargeAndUp ? 'link' : 'button'}
              onClick={onMwAcctSettingsClick}
              href="/settings/account"
              tabIndex={open ? 0 : -1}
              data-qa-id="accountSettingsAcctDrawerItem"
            />
            <AcctDrawerItem
              icon={<ClickOutLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              text="Sign out"
              onClick={onLogout}
              type="button"
              tabIndex={open ? 0 : -1}
              data-qa-id="signOutAcctDrawerItem"
            />
            <AcctDetailsProCTA user={user} />
          </>
        ) : (
          <>
            <AcctDrawerItem
              href="/profile/edit"
              text="Edit profile"
              icon={<EditLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="editProfileAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/profile"
              text="Account info"
              icon={<AccountLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="accountInfoAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/settings/blocks"
              text="Manage block list"
              icon={<AlertLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="manageBlockListAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/settings/data_privacy"
              text="Data privacy"
              icon={<LockLineIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="dataPrivacyAcctDrawerItem"
            />
            <AcctDrawerItem
              href="/settings/pm_order_history"
              text="Your orders"
              icon={<DocumentIcon ratio={isExtraLargeAndUp ? 0.75 : 1} />}
              tabIndex={open ? 0 : -1}
              data-qa-id="yourOrdersAcctDrawerItem"
            />
            <AcctDetailsProCTA user={user} />
          </>
        )}
      </div>
    </div>
  );
};

AccountDetailsDrawer.propTypes = {
  open: PropTypes.bool,
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onLogout: PropTypes.func,
};
